import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { teamService } from '../../../services/teamService';
import { useAuth } from '../../../contexts/AuthContext';

const TeamTab = () => {
  const { t } = useTranslation();
  const { user, userProfile, activeCompanyId, isSuperAdmin } = useAuth();
  const canManageTeam = isSuperAdmin || userProfile?.role === 'admin';
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteError, setInviteError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [companyId, setCompanyId] = useState(null);

  const roleOptions = [
    { value: 'admin', label: t('accountSettings.team.roles.admin.label'), description: t('accountSettings.team.roles.admin.description') },
    { value: 'member', label: t('accountSettings.team.roles.member.label'), description: t('accountSettings.team.roles.member.description') },
    ...(isSuperAdmin ? [{
      value: 'super_admin',
      label: t('accountSettings.team.roles.superAdmin.label'),
      description: t('accountSettings.team.roles.superAdmin.description')
    }] : [])
  ];

  useEffect(() => {
    loadTeamData();
  }, [user, activeCompanyId]);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      const effectiveCompanyId = activeCompanyId || companyId;

      if (!effectiveCompanyId) {
        const { userService } = await import('../../../services/userService');
        const profile = await userService?.getCurrentProfile();
        if (profile?.companyId) {
          setCompanyId(profile?.companyId);
          const members = await teamService?.getTeamMembers(profile?.companyId);
          setTeamMembers(members || []);
          const invitations = await teamService?.getInvitations(profile?.companyId);
          setPendingInvitations(invitations || []);
        }
      } else {
        setCompanyId(effectiveCompanyId);
        const members = await teamService?.getTeamMembers(effectiveCompanyId);
        setTeamMembers(members || []);
        const invitations = await teamService?.getInvitations(effectiveCompanyId);
        setPendingInvitations(invitations || []);
      }
    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail?.trim()) {
      setInviteError(t('accountSettings.team.errors.emailRequired'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(inviteEmail)) {
      setInviteError(t('accountSettings.team.errors.emailInvalid'));
      return;
    }

    if (!companyId) {
      setInviteError(t('accountSettings.team.errors.companyNotFound'));
      return;
    }

    setIsSending(true);
    setInviteError(''); // Clear any previous errors
    try {
      await teamService?.inviteTeamMember(companyId, inviteEmail, inviteRole);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      setInviteError('');
      await loadTeamData();
    } catch (error) {
      console.error('Invitation error:', error);
      console.error('Error object keys:', Object.keys(error || {}));
      console.error('Error message:', error?.message);
      console.error('Error originalDetails:', error?.originalDetails);
      console.error('Error originalError:', error?.originalError);
      
      // Show the exact error message from the API
      // Prioritize originalDetails (most specific), then message, then fallback
      let errorMessage = error?.originalDetails || error?.message || error?.originalError || t('accountSettings.team.errors.invitationFailed');
      
      // Remove generic fallback messages
      if (errorMessage === 'Failed to send invitation' || errorMessage === 'Failed to send invitation email') {
        // Try to get a more specific message
        errorMessage = error?.originalDetails || error?.originalError || t('accountSettings.team.errors.invitationFailed');
      }
      
      // Check if this is a configuration error and add helpful context
      if (error?.isConfigurationError) {
        errorMessage = errorMessage + ' See the Edge Function README for deployment instructions: supabase/functions/invite-team-member/README.md';
      }
      
      console.log('Setting error message in UI:', errorMessage);
      setInviteError(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await teamService?.cancelInvitation(invitationId);
      await loadTeamData();
    } catch (error) {
      console.error('Error canceling invitation:', error);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm(t('accountSettings.team.confirmRemove'))) return;
    
    try {
      await teamService?.removeMember(memberId);
      await loadTeamData();
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-accent/10 text-accent border-accent/20',
      member: 'bg-secondary/10 text-secondary border-secondary/20',
      super_admin: 'bg-purple-500/10 text-purple-600 border-purple-500/20'
    };

    const labels = {
      admin: t('accountSettings.team.roles.admin.label'),
      member: t('accountSettings.team.roles.member.label'),
      super_admin: t('accountSettings.team.roles.superAdmin.label')
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded border ${styles?.[role] || styles.member}`}>
        {labels?.[role] || role?.charAt(0)?.toUpperCase() + role?.slice(1)}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-success/10 text-success border-success/20',
      pending: 'bg-warning/10 text-warning border-warning/20'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded border ${styles?.[status]}`}>
        {t(`accountSettings.team.status.${status}`)}
      </span>
    );
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name?.split(' ')?.map(word => word?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('accountSettings.team.dateFormat.never');
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-text">{t('accountSettings.team.title')}</h2>
          <p className="text-sm text-text/60 mt-1">{t('accountSettings.team.subtitle')}</p>
        </div>
        {canManageTeam && (
          <Button
            onClick={() => setShowInviteModal(true)}
            className="w-full sm:w-auto"
          >
            <Icon name="user-plus" className="w-4 h-4 mr-2" />
            {t('accountSettings.team.addMember')}
          </Button>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left px-4 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold text-foreground">
                  {t('accountSettings.team.table.member')}
                </th>
                <th className="text-left px-4 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold text-foreground">
                  {t('accountSettings.team.table.role')}
                </th>
                <th className="text-left px-4 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold text-foreground">
                  {t('accountSettings.team.table.status')}
                </th>
                <th className="text-left px-4 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold text-foreground hidden lg:table-cell">
                  {t('accountSettings.team.table.joined')}
                </th>
                <th className="text-right px-4 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold text-foreground">
                  {t('accountSettings.team.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {teamMembers?.length === 0 && pendingInvitations?.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 md:px-6 py-8 text-center text-muted-foreground">
                    {t('accountSettings.team.table.empty')}
                  </td>
                </tr>
              ) : (
                <>
                  {teamMembers?.map((member) => (
                    <tr key={member?.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-accent/20 flex items-center justify-center text-accent text-xs md:text-sm font-semibold flex-shrink-0">
                            {getInitials(member?.fullName)}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm md:text-base font-medium text-foreground truncate">
                              {member?.fullName || 'Unknown'}
                            </div>
                            <div className="text-xs md:text-sm text-muted-foreground truncate">
                              {member?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        {getRoleBadge(member?.role)}
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        {getStatusBadge('active')}
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-xs md:text-sm text-muted-foreground hidden lg:table-cell">
                        {formatDate(member?.createdAt)}
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <div className="flex items-center justify-end gap-2">
                          {(isSuperAdmin || userProfile?.role === 'admin') && member?.id !== user?.id && (
                            <button
                              onClick={() => handleRemoveMember(member?.id)}
                              className="p-1.5 md:p-2 hover:bg-destructive/10 rounded transition-colors"
                              aria-label="Remove member"
                            >
                              <Icon name="Trash2" size={16} className="text-destructive" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {pendingInvitations?.map((invitation) => (
                    <tr key={invitation?.id} className="hover:bg-muted/30 transition-colors bg-muted/10">
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-warning/20 flex items-center justify-center text-warning text-xs md:text-sm font-semibold flex-shrink-0">
                            <Icon name="Mail" size={16} />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm md:text-base font-medium text-foreground truncate">
                              {t('accountSettings.team.invitation.pendingInvitation')}
                            </div>
                            <div className="text-xs md:text-sm text-muted-foreground truncate">
                              {invitation?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        {getRoleBadge(invitation?.role)}
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        {getStatusBadge('pending')}
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-xs md:text-sm text-muted-foreground hidden lg:table-cell">
                        {t('accountSettings.team.invitation.invited')} {formatDate(invitation?.createdAt)}
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <div className="flex items-center justify-end gap-2">
                          {canManageTeam && (
                            <button
                              onClick={() => handleCancelInvitation(invitation?.id)}
                              className="p-1.5 md:p-2 hover:bg-destructive/10 rounded transition-colors"
                              aria-label={t('accountSettings.team.invitation.cancelInvitation')}
                            >
                              <Icon name="X" size={16} className="text-destructive" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-muted/50 border border-border rounded-lg p-4 md:p-6">
        <div className="flex items-start gap-3 md:gap-4">
          <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h3 className="text-sm md:text-base font-semibold text-foreground">
              {t('accountSettings.team.roles.title')}
            </h3>
            <ul className="text-xs md:text-sm text-muted-foreground space-y-1">
              <li>• <strong>{t('accountSettings.team.admin')}:</strong> {t('accountSettings.team.roles.adminDescription')}</li>
              <li>• <strong>{t('accountSettings.team.member')}:</strong> {t('accountSettings.team.roles.memberDescription')}</li>
            </ul>
          </div>
        </div>
      </div>

      {showInviteModal && (
        <>
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
            onClick={() => {
              setShowInviteModal(false);
              setInviteError('');
            }}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-lg shadow-elevation-xl w-full max-w-md">
              <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
                <h3 className="text-lg md:text-xl font-semibold text-foreground">
                  {t('accountSettings.team.modal.title')}
                </h3>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteError('');
                  }}
                  className="p-1 hover:bg-muted rounded transition-colors"
                  aria-label={t('accountSettings.team.modal.closeModal')}
                >
                  <Icon name="X" size={20} className="text-muted-foreground" />
                </button>
              </div>

              <div className="p-4 md:p-6 space-y-4 md:space-y-6">
                <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 text-xs md:text-sm text-accent">
                  <p>{t('accountSettings.team.modal.invitationInfo')}</p>
                </div>

                <Input
                  label={t('accountSettings.team.emailAddress')}
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => {
                    setInviteEmail(e?.target?.value);
                    setInviteError('');
                  }}
                  error={inviteError}
                  placeholder={t('accountSettings.team.colleagueEmailPlaceholder')}
                  required
                />

                <Select
                  label={t('accountSettings.team.role')}
                  options={roleOptions}
                  value={inviteRole}
                  onChange={setInviteRole}
                  required
                />

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="default"
                    onClick={handleInvite}
                    loading={isSending}
                    iconName="Mail"
                    iconPosition="left"
                    className="w-full sm:flex-1"
                  >
                    {t('accountSettings.team.sendInvitation')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowInviteModal(false)}
                    disabled={isSending}
                    className="w-full sm:w-auto"
                  >
                    {t('accountSettings.team.cancel')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TeamTab;